import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { wordApi } from "@/api/word.api";
import { useAdminScope } from "@/hooks/use-admin-scope";
import { useApp } from "@/hooks/use-app-context";
import { useToast } from "@/hooks/use-toast";
import { EXCEL_FILES_QUERY_KEY } from "./excel-files.constants";
import { isExcelImportReady } from "./excel-file-monitor";
import { excelPhraseErrorMessage } from "./excel-phrase-errors";

export function useExcelImport() {
  const { t } = useApp();
  const { visibleLanguages } = useAdminScope();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguage] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const reset = useCallback(() => {
    setLanguage(visibleLanguages[0]?.code ?? "");
    setFile(null);
  }, [visibleLanguages]);

  const setOpen = useCallback(
    (next: boolean) => {
      setIsOpen(next);
      if (next) reset();
    },
    [reset],
  );

  const mutation = useMutation({
    mutationFn: () => {
      if (!file || !isExcelImportReady(language, file)) {
        throw new Error("IMPORT_INPUT_REQUIRED");
      }
      return wordApi.uploadExcel(language, file);
    },
    onSuccess: (result) => {
      if (result.message !== "EXCEL_IMPORT_UPLOADED") return;
      toast({
        title: t.success,
        description:
          result.data.approvalStatus === "PENDING"
            ? t.admin.excelFiles.importPendingSuccess
            : t.admin.excelFiles.importActiveSuccess,
      });
      setIsOpen(false);
      void queryClient.invalidateQueries({ queryKey: EXCEL_FILES_QUERY_KEY });
    },
    onError: (error) => {
      toast({
        title: t.error,
        description: excelPhraseErrorMessage(error, t.admin.excelPhrases.serverErrors),
        variant: "destructive",
      });
    },
  });

  return {
    isOpen,
    setOpen,
    language,
    setLanguage,
    file,
    setFile,
    languages: visibleLanguages,
    isReady: isExcelImportReady(language, file),
    isSubmitting: mutation.isPending,
    submit: mutation.mutate,
  };
}

